<script setup>
import { computed, nextTick, ref } from 'vue';
import HdIcon from './HdIcon.vue';
import MarginNote from './MarginNote.vue';
import NoteRow from './NoteRow.vue';
import { steps, saveStep, removeStep } from '../store.js';
import { remove as removeWithUndo } from '../core/undo.js';

/** One step of the recipe, set on ruled paper, with whatever the cook said
 *  during that step sitting in the margin beside it.
 *
 *  THE ROW FLOWS. It has no height of its own — not a fixed one, not a minimum
 *  beyond a single rule. Every line box in it is line-height: var(--hd-rule),
 *  so a one-line step is 28px tall, a three-line step is 84px, and the next
 *  step starts on the next ruled line either way. Anything in here that
 *  acquires a height of its own puts every step below it off the lines, which
 *  is why the number, the menu mark and the margin notes' tap targets are all
 *  out of the flow. */
const props = defineProps({
  /** { id, body, position } */
  step: { type: Object, required: true },
  /** What the reader sees: 1, 2, 3 down the page.
   *
   *  NOT `position`, which is the sort key. The two are usually the same and
   *  must not be assumed to be: a recipe whose rows were written out of order,
   *  or repositioned by a half-finished renumber, would print a method that
   *  skips a number — and a cook who reads "1, 2, 4" goes looking for the step
   *  they missed. The page counts the rows it is drawing. */
  number: { type: [Number, String], default: null },
  /** margin-note rows for this step */
  notes: { type: Array, default: () => [] },
  cookName: { type: String, default: '' },
  playingNoteId: { type: [String, Number], default: null },
  progress: { type: Number, default: 0 },
  expanded: { type: Boolean, default: false },
  /** Capture only: tapping the words opens the editor and the step wears its
   *  own menu mark. The sheet is READ FROM while cooking, so there the words
   *  are words: a stray thumb on a step must not open an editor over the thing
   *  you are trying to read, and there is no chrome on the row at all. */
  editable: { type: Boolean, default: false },
});

defineEmits(['play', 'expand', 'manage', 'record', 'pin']);

/* The page opens the step's menu, and "Fix the words" has to reach back in
   here — the editor and its undo live in this component. */
defineExpose({ beginEdit: () => beginEdit(), deleteStep: () => deleteStep() });

/* Two fit the margin without crowding the step. The rest go behind a count.
   Two notes now cost two ruled lines in the margin rather than four, so a step
   of two lines or more carries them without adding a single pixel to the row. */
const shown = computed(() => (props.expanded ? props.notes : props.notes.slice(0, 2)));
const hidden = computed(() => Math.max(0, props.notes.length - shown.value.length));

/* Store ids are numbers; a page may hand us a string. Compare as strings so a
   playing note is still recognised as playing. */
const same = (a, b) => String(a) === String(b);
const isPlaying = (id) => props.playingNoteId != null && same(props.playingNoteId, id);

/* THE MARGIN IS FOR READING. WHILE EDITING, THE NOTES BECOME A LIST.
 *
 * In the margin a note is a mark: no label, no duration, nothing to act on
 * except a press-and-hold that teaches nobody. That is right on the page you
 * cook from — it has to keep looking like paper — and wrong on the screen
 * where you are fixing things, where four unlabelled discs in a row is a
 * puzzle. NoteRow is the editing form; the reasoning lives there.
 */

// ------------------------------------------------------------------ editing
//
// FIXING THE WORDS LIVES HERE, IN THE COMPONENT, RATHER THAN BEING EMITTED.
//
// Not because a `save` emit would be wrong, but because both screens that
// render a step — capture and the recipe sheet — would then have to be changed
// in step with this one to get it, and a step that cannot be corrected on the
// screen you happen to be looking at is the bug this exists to close. The step
// is a row in a shared store; the component that draws it can write to it.
//
// A step is typed one-handed, in a kitchen, beside someone who is still
// talking. The first draft is wrong roughly as often as it is right, and until
// now the only cure was to leave it wrong forever.

const editing = ref(false);
const draft = ref('');
const box = ref(null);

/* An unfocused textarea styled to look like the paragraph is not the same as
   the paragraph: it takes a tap to reach the caret and it fights the ruled
   lines while it is empty. So the resting state is a real button carrying the
   real words, and the field only exists while it is being typed into. */
function beginEdit() {
  draft.value = props.step.body ?? '';
  editing.value = true;
  nextTick(() => {
    const el = box.value;
    if (!el) return;
    grow();
    el.focus();
    /* Caret at the end, not selecting everything: this is a correction, and
       the common one is a letter or two, not a rewrite. */
    try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* not supported */ }
  });
}

/* The field grows a whole ruled line at a time, because its line-height IS the
   rule pitch and it carries no padding or border — so scrollHeight is always a
   multiple of it and the writing under this step never drifts off the lines. */
function grow() {
  const el = box.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function commitEdit() {
  if (!editing.value) return;
  editing.value = false;
  /* One paragraph, always: a step's line-height is the rule pitch, and a
     pasted newline would put half of it between two rules. Runs of whitespace
     collapse for the same reason. */
  const next = String(draft.value).replace(/\s+/g, ' ').trim();
  /* Emptying the box is not a delete. Delete is a button, it says what it will
     do, and it can be taken back — a step silently vanishing because a thumb
     landed on "select all" is not the same thing at all. */
  if (!next || next === (props.step.body ?? '')) return;
  saveStep({ ...props.step, body: next });
}

function cancelEdit() {
  editing.value = false;
}

/* The delete control sits inside the open editor, so pressing it takes focus
   off the field and blur would tear the editor down before the click landed.
   This holds the editor open across that one gesture. */
const pressing = ref(false);
function onBlur() {
  if (pressing.value) return;
  commitEdit();
}

// ----------------------------------------------------------------- deleting

const siblings = computed(() =>
  steps.value
    .filter((s) => same(s.recipeId, props.step.recipeId))
    .slice()
    .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)),
);

/* WHAT HAPPENS TO WHAT SHE SAID: nothing. The notes stay.
 *
 * A margin note carries a recording, and a recording is the only thing in this
 * app that cannot be made again once the person who made it is gone. Deleting
 * the notes with the step would mean a mistyped step, tidied up, quietly taking
 * her voice with it — the worst outcome this app has. So the note keeps
 * pointing at a step id that no longer exists, and the recipe sheet already
 * expects exactly that: an orphan surfaces at the top of the margin rather than
 * disappearing. The toast says so, so nobody has to guess. */
function deleteStep() {
  const victim = { ...props.step };
  const kept = props.notes.length;

  /* The positions as they stand, so Undo can put them back. Renumbering has to
     happen NOW — a method that reads 1, 2, 4 while a toast is up is a method
     with a step missing, which is the one thing the reader must not think. */
  const order = siblings.value.map((s) => ({ id: s.id, position: s.position }));
  const rest = siblings.value.filter((s) => !same(s.id, victim.id));

  /* Off the screen immediately; out of IndexedDB only when the window closes.
     That ordering is undo.js's whole point, and it is what lets Undo restore
     this row under its ORIGINAL id — margin notes point at that id, and an
     "undo" that handed the step a fresh one would orphan every note on it. */
  steps.value = steps.value.filter((s) => !same(s.id, victim.id));
  rest.forEach((s, i) => {
    if (Number(s.position) !== i + 1) saveStep({ ...s, position: i + 1 });
  });

  const marks = kept === 1
    ? ' The note in its margin is kept.'
    : kept > 1
      ? ` The ${kept} notes in its margin are kept.`
      : '';

  removeWithUndo({
    text: `Step ${victim.position} deleted.${marks}`,
    commit: () => removeStep(victim.id),
    revert: () => {
      steps.value = [...steps.value, victim];
      saveStep(victim);
      order.forEach((p) => {
        const row = steps.value.find((s) => same(s.id, p.id));
        if (row && Number(row.position) !== Number(p.position)) {
          saveStep({ ...row, position: p.position });
        }
      });
    },
  });
}

function pressDelete() {
  pressing.value = false;
  editing.value = false;
  deleteStep();
}
</script>

<template>
  <li class="hd-row">
    <!-- A column of 28px slots, one per note, so two notes on a step occupy
         two ruled lines in the margin and line up with the two lines of
         writing beside them. Empty, it is an empty cell: no min-height, no
         spacer, nothing to make a step with nothing in its margin taller than
         the words it contains. -->
    <div class="margin">
      <MarginNote
        v-for="note in editable ? [] : shown"
        :key="note.id"
        :note="note"
        :cook-name="cookName"
        :playing="isPlaying(note.id)"
        :progress="isPlaying(note.id) ? progress : 0"
        @play="$emit('play', $event)"
        @manage="$emit('manage', $event)"
      />
      <button
        v-if="hidden && !editable"
        class="more hd-figure"
        type="button"
        :aria-label="`Show ${hidden} more note${hidden > 1 ? 's' : ''} on step ${step.position}`"
        @click="$emit('expand', step.id)"
      >
        +{{ hidden }}
      </button>
    </div>

    <div class="hd-row-body body" :class="{ 'is-editing': editing, 'is-editable': editable }">
      <!-- The step number, which the method never showed. `position` existed
           from the first day and only ever ordered the rows; a method you cook
           from needs to say which step you are on, out loud, so you can look
           away at a pan and find your place again.

           Beside the writing rather than over it, on the same line box, at the
           same 28px line-height — so it is part of the row's flow and adds
           nothing to its height. Archivo and tabular, like every other number
           here, so 9 and 10 line up their left edges instead of shuffling the
           writing sideways. -->
      <span class="step-n hd-figure" aria-hidden="true">{{ number ?? step.position }}</span>

      <!-- ON CAPTURE the words are the control: you are writing, the first
           draft is usually wrong, and reaching the caret should cost one tap.
           There is no pencil icon and no edit row in the chrome of every step,
           because on a page whose whole subject is handwriting the thing you
           press to change what is written is what is written.

           ON THE RECIPE SHEET they are a paragraph. That screen is read from
           while cooking, with wet hands, and a step that opens an editor when
           a thumb lands on it is a step you have to escape from mid-recipe.
           Fixing the words there is a named item in the step's menu. -->
      <button
        v-if="editable && !editing"
        class="text"
        type="button"
        :aria-label="`Step ${step.position}: ${step.body || 'empty'}. Tap to fix the words.`"
        @click="beginEdit"
      >{{ step.body }}</button>
      <p v-else-if="!editing" class="text">{{ step.body }}</p>

      <template v-else>
        <textarea
          ref="box"
          v-model="draft"
          class="text field"
          rows="1"
          :aria-label="`Step ${step.position}`"
          @input="grow"
          @blur="onBlur"
          @keydown.enter.prevent="commitEdit"
          @keydown.esc.prevent="cancelEdit"
        ></textarea>

        <!-- Only while the step is open. A bin beside every row would be
             permanent chrome on a page that is meant to read as paper, and
             deleting a step is not something anyone does in passing. -->
        <!-- Everything a step can have done to it, while it is open and only
             while it is open. A row of marks beside every step would be
             permanent chrome on a page meant to read as paper; here they cost
             nothing until you are already changing this step. -->
        <div class="edit-actions">
          <button class="act" type="button" :aria-label="`Record a note on step ${step.position}`" @mousedown.prevent @click="$emit('record', step.id)">
            <HdIcon name="record" :size="12" />
            <span>Record</span>
          </button>
          <button class="act" type="button" :aria-label="`Write a note on step ${step.position}`" @mousedown.prevent @click="$emit('pin', step.id)">
            <HdIcon name="lines" :size="13" :weight="1.9" />
            <span>Write a note</span>
          </button>
          <button
            class="act drop"
            type="button"
            :aria-label="`Delete step ${step.position}`"
            @pointerdown="pressing = true"
            @pointerleave="pressing = false"
            @pointercancel="pressing = false"
            @mousedown.prevent
            @click="pressDelete"
          >
            <HdIcon name="trash" :size="13" />
            <span>Delete</span>
          </button>
        </div>
      </template>

      <!-- ONE control per step, and only where a step can be changed.
           Add a note, fix the words, delete — so there is exactly one thing to
           learn per row, and on the sheet there is nothing to learn at all.

           A PENCIL THAT EDITS, NOT A MENU THAT OFFERS TO.
           It used to open a list of four, whose first useful item was "Fix the
           words" — which tapping the words already does. A control whose main
           job is to reach something one tap away is a control in the way, and
           three dots say only "there is more here", never what.
           So: pencil means change these words, and it changes them. The other
           three things a step can do — record a note on it, write one on it,
           delete it — appear inside the open editor, where you are already
           looking at the step they act on.

           Drawn, from the one icon file, never a typed character: '···' is
           whatever the device's fallback font decided, at whatever baseline it
           chose, and it recoloured and re-aligned on every phone.

           OUT OF THE FLOW, on purpose: in the flow it adds its own height to
           every row and knocks each following step off the ruled lines. It
           hangs 8px above and below the first rule to make its 44px target
           without occupying more than the 28px line it sits on. -->
      <button
        v-if="editable && !editing"
        class="step-menu"
        type="button"
        :aria-label="`Fix the words of step ${step.position}`"
        @click="beginEdit"
      >
        <HdIcon name="pencil" :size="16" />
      </button>

      <!-- THE NOTES, WHILE EDITING: named, playable, and actionable without a
           gesture. See NoteRow for why the margin form is not enough here. -->
      <ul v-if="editable && notes.length" class="note-rows">
        <NoteRow
          v-for="note in notes"
          :key="note.id"
          :note="note"
          :cook-name="cookName"
          :playing="isPlaying(note.id)"
          @play="$emit('play', $event)"
          @manage="$emit('manage', $event)"
        />
      </ul>
    </div>
  </li>
</template>

<style scoped>
/* ITS OWN LINE, UNDER THE WORDS. `.body` is a flex row — the number beside
   the writing — so a bare <ul> in it became a third column and the notes
   rendered alongside the step instead of beneath it. `flex: 0 0 100%` with a
   wrapping parent puts the list on the next line, indented past the number so
   it sits under the words it belongs to rather than under the digit. */
.note-rows {
  flex: 0 0 100%;
  width: 100%;
  margin: 0;
  padding: 0 0 0 21px;
  list-style: none;
}
/* The grid itself is .hd-row in app.css — var(--hd-margin-w) then the writing —
   so that every kind of row on the sheet shares one left edge. Nothing is added
   here, and deliberately no red rule: the margin line is one element drawn down
   the whole field (.hd-field-rule), not a border repeated per row that breaks
   wherever a row happens to be short. */

/* No min-height, no gap, no padding. Each MarginNote is exactly one rule tall,
   so this column is always a whole number of ruled lines and can never be the
   thing that decides how tall a step is — which it used to be, and which is why
   every step took two lines whether its words needed them or not. */
.margin {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* One ruled line, like the notes above it. */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--hd-rule);
  padding: 0;
  background: none;
  border: 0;
  color: var(--hd-margin);
  cursor: pointer;
}

/* THE ARITHMETIC, which is why the writing sits on the lines at all.
 *
 * There is none left to do. The number and the words share one line box whose
 * line-height IS the rule pitch, so the body is 28px per line of writing and
 * nothing else — no bottom padding to close a sum, no floor of two lines, no
 * control standing underneath. A step is as tall as what it says.
 *
 * The right padding, on capture only, is the menu mark's lane, so a long step's
 * last line never runs underneath it. On the sheet there is no mark and the
 * writing uses the full width. */
.body {
  position: relative;
  display: flex;
  /* Wrapping is what lets the note list drop to its own line under the step
     rather than becoming a third column beside the number and the words. */
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
}
/* The menu mark's lane, taken out of the WRITING rather than out of the body's
   padding — the tin skin re-declares .hd-row-body's padding at a higher
   specificity, and a lane that survives in one skin and not the other is a step
   whose last line runs under the mark on half the app. */
.body.is-editable .text { margin-right: 30px; }

/* 13px is two tabular digits at 12px and not a pixel more: the writing starts
   at one left edge whether the step is 1 or 10, and the number hangs to its
   left the way it does in a notebook. */
.step-n {
  flex: none;
  width: 13px;
  /* The one number in the app that was not --hd-t-figure. It sits in a 13px
     margin column, so it is the token minus nothing: tabular figures at 13
     still fit, and now every number on the page is one size. */
  font-size: var(--hd-t-figure);
  line-height: var(--hd-rule);
  text-align: right;
  color: var(--hd-pencil);
}

/* Out of the flow — see the template. 44px of target centred on a 28px line. */
.step-menu {
  position: absolute;
  top: -8px;
  right: -6px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--hd-pencil);
  cursor: pointer;
}
.step-menu:hover,
.step-menu:focus-visible { color: var(--hd-margin); }

/* ONE rule for both the written step and the box you fix it in, because the
   moment they differ the words move when you tap them. Everything that decides
   where a glyph lands is here: family, size, the line-height that IS the rule
   pitch, no padding, no border, the same wrapping width.
   A <button> and a <textarea> otherwise inherit none of it — both carry
   browser type of their own — which is exactly how an edit-in-place breaks a
   ruled page. */
.text {
  /* `flex: 1 1 0`, NOT `1 1 auto`, and the difference is the default rendering
     of this screen. With an auto basis the flex algorithm starts from the
     text's max-content width, finds it wider than the row, and — because the
     body wraps so the note list can drop to its own line — puts the WRITING on
     the next line instead of shrinking it. The step's number was then left
     alone on a blank rule with the voice disc beside it, pointing at nothing,
     while the words started underneath. Nearly every real step runs to more
     than one line, so that was not an edge case; it was the norm.
     A zero basis makes it shrink to the space available, which is what a
     line of writing on a page does. */
  flex: 1 1 0;
  min-width: 0;
  display: block;
  max-width: 34em;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: var(--hd-t-step);
  font-weight: inherit;
  line-height: var(--hd-rule);
  color: var(--hd-ink);
  text-align: left;
  /* Wrapping has to match too: a button collapses whitespace by default and a
     textarea does not, so a step ending in a space would wrap in one and not
     the other. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* A BUTTON CENTRES ITS OWN CONTENTS, and `display: block` does not stop it.
   It cost nothing while a one-line step was exactly one line tall, and cost
   14px the moment anything gave the button spare height — the ruled line ran
   straight through the middle of the writing, which is precisely the failure
   this component exists to avoid. A column flex box with the content packed to
   the start is the only reliable way to make a button's text start at its top
   edge. `stretch` keeps the anonymous line box full width, so it wraps at
   exactly the same place the textarea does.

   NO min-height. A 56px floor here is what used to make a one-word step take
   two ruled lines. The 44px target on capture is the step's menu mark, which
   is what "Fix the words" is named in. */
button.text {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
}

/* The caret is already the margin red (app.css), the keyboard is up, and the
   words have not moved — that is the whole of the "you are editing" signal.
   A box drawn around the field would sit across the ruled lines.
   padding:0 and border:0 are required — any padding shifts scrollHeight off
   the 28px grid and the step below lands between lines. outline:none removes
   the native iOS focus ring for the same reason. */
.field {
  min-height: var(--hd-rule);
  padding: 0;
  border: 0;
  outline: none;
  resize: none;
  overflow: hidden;
  -webkit-appearance: none;
  appearance: none;
}

/* IN THE FLOW, on its own line under the words.
 *
 * It used to be absolutely positioned against the bottom of `.body` with a
 * matching bottom padding — which worked while `.body` held nothing but the
 * step. It now also holds the step's notes, so "the bottom of the body" moved
 * under the note list and the three actions landed on top of it.
 *
 * `flex: 0 0 100%` in the wrapping body puts them on their own line, directly
 * under the writing they act on, and two ruled lines tall so the notes below
 * stay on the lines. */
.edit-actions {
  flex: 0 0 100%;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-left: 21px;
  height: calc(var(--hd-rule) * 2);
}

/* The three things you can do to the step you are editing. Pencil-coloured
   rather than red: the one red in this app means the margin rule and
   recording, and only Record is one of those. */
.act {
  display: inline-flex;
  /* Top-aligned for the same reason the add buttons are: a control two rules
     tall with its contents centred puts its words between the lines. */
  align-items: flex-start;
  gap: 6px;
  height: calc(var(--hd-rule) * 2);
  padding: 0 12px 0 0;
  /* Transparent, not paper-coloured. An opaque box two rules tall punched
     three gaps out of the ruled line beneath it — the page looked torn. */
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  color: var(--hd-pencil);
  text-align: left;
  cursor: pointer;
}
/* Record is the app's whole reason for existing and is the one of the three
   that is genuinely the red. */
.act > * { height: var(--hd-rule); display: flex; align-items: center; }
.act span { line-height: var(--hd-rule); }
/* Record is the app's whole reason for existing and is genuinely the red. */
.act:first-child { color: var(--hd-margin); }

/* DELETE HAD NO STYLING AT ALL. The class survived a rename — `.drop` was the
   old block, `.act` the new one, and the element kept both — so the one
   irreversible control in editing mode rendered identical to "Write a note",
   in the same pencil, 16px away from it, in the mode you are in while someone
   is still talking and you are typing one-handed.
   Pushed to the far end of the row so real distance separates it from the two
   constructive actions, and given the red, because here red is the warning
   rather than the recording. */
.act.drop {
  margin-left: auto;
  padding-right: 0;
  color: var(--hd-margin);
}
.act:focus-visible { outline: 2px solid var(--hd-focus); outline-offset: 2px; }
</style>
